import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isValidSlug } from '@photobooth/shared';
import { resetBoothPassword } from '../api/client';
import { rememberUnlocked } from '../builder/passwordGate';
import { Button } from '../ui/Button';
import { FooterCredit } from '../ui/FooterCredit';
import { logger } from '../utils/log';
import { trackEvent } from '../utils/analytics';

const log = logger('booth-reset');

type Phase = 'form' | 'busy' | 'done' | 'error';

export function BoothPasswordResetPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const token = search.get('token') ?? '';
  const navigate = useNavigate();

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);

  const slugOk = isValidSlug(slug);
  const tokenOk = /^[A-Z2-9]{8}$/.test(token.toUpperCase());
  const mismatch = pw.length > 0 && confirm.length > 0 && pw !== confirm;

  const submit = async () => {
    if (pw.length === 0) {
      setError('Pick a password.');
      return;
    }
    if (pw.length > 64) {
      setError('Password must be 64 characters or fewer.');
      return;
    }
    if (pw !== confirm) {
      setError('Passwords don’t match.');
      return;
    }
    setPhase('busy');
    setError(null);
    try {
      await resetBoothPassword(slug, { otp: token.toUpperCase(), password: pw });
      trackEvent('password_reset_completed', { slug });
      // Mark this booth as unlocked so the user can continue without
      // entering the password they just set.
      rememberUnlocked(slug);
      setPhase('done');
    } catch (err) {
      log.warn('reset failed', err);
      setError(err instanceof Error ? err.message : 'Reset failed.');
      setPhase('error');
    }
  };

  if (!slugOk || !tokenOk) {
    return (
      <div style={centered}>
        <h1>Bad reset link</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 380, textAlign: 'center' }}>
          This link is malformed. Open the link in your email again, or start a
          new reset.
        </p>
        <Button variant="primary" onClick={() => navigate(`/${slug || ''}`)}>
          Back to booth
        </Button>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div style={centered}>
        <h1>Password updated</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 380, textAlign: 'center' }}>
          Your new password is set. You’re already signed in here.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate(`/${slug}/gallery`)}>
            Open gallery
          </Button>
          <Button variant="primary" onClick={() => navigate(`/${slug}`)}>
            Open booth
          </Button>
        </div>
        <FooterCredit />
      </div>
    );
  }

  return (
    <div style={centered}>
      <h1>Set a new password</h1>
      <p style={{ color: 'var(--text-dim)', maxWidth: 380, textAlign: 'center' }}>
        For <code>/{slug}</code>. The magic link is good for one use.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: 'grid', gap: 12, width: '100%', maxWidth: 360 }}
      >
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus
          placeholder="New password"
          maxLength={64}
          autoComplete="new-password"
          style={inputStyle(false)}
        />
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          maxLength={64}
          autoComplete="new-password"
          style={inputStyle(mismatch)}
        />
        {(error || mismatch) && (
          <div style={{ fontSize: 13, color: 'var(--danger)' }}>
            {error ?? 'Passwords don’t match.'}
          </div>
        )}
        <Button
          variant="primary"
          type="submit"
          disabled={phase === 'busy' || pw.length === 0 || pw !== confirm}
        >
          {phase === 'busy' ? 'Saving…' : 'Save password'}
        </Button>
      </form>
      <FooterCredit />
    </div>
  );
}

function inputStyle(error: boolean): React.CSSProperties {
  return {
    padding: '12px 14px',
    borderRadius: 10,
    border: error ? '1px solid var(--danger)' : '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(0,0,0,0.45)',
    color: 'var(--text)',
    fontSize: 15,
  };
}

const centered: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  color: 'var(--text)',
};
